/* Product-only Shopify suggestions. The GET form remains usable without JavaScript. */
(() => {
  if (customElements.get('cp-product-search')) return;
  class ProductSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input = this.querySelector('[data-cp-query]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-cp-suggestions]');
      this.status = this.querySelector('[data-cp-status]');
      this.reset = this.querySelector('[data-cp-reset]');
      this.browse = this.querySelector('[data-cp-browse]');
      this.dialog = this.querySelector('[data-cp-dialog]');
      this.trigger = this.dataset.trigger ? document.getElementById(this.dataset.trigger) : null;
      this.cache = new Map();
      this.sequence = 0;
      this.input.setAttribute('aria-controls', this.results.id);
      this.reset.hidden = !this.input.value;
      this.input.addEventListener('input', () => this.onInput(), options);
      this.addEventListener('keydown', event => this.onKey(event), options);
      this.input.addEventListener('focus', () => { if (this.input.value.trim() && this.results.hidden) this.onInput(); }, options);
      this.reset.addEventListener('click', () => {
        this.input.value = ''; this.onInput(); this.input.focus();
      }, options);
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
        else this.input.value = this.input.value.trim();
      }, options);
      if (this.dialog && this.trigger && typeof this.dialog.showModal === 'function') {
        this.trigger.setAttribute('aria-haspopup', 'dialog');
        this.trigger.setAttribute('aria-controls', this.dialog.id);
        this.trigger.addEventListener('click', event => {
          if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault(); this.dialog.showModal();
          document.documentElement.classList.add('cp-search-is-open');
          this.fitDialog();
          this.input.focus();
        }, options);
        this.querySelector('[data-cp-close]').addEventListener('click', () => this.dialog.close(), options);
        this.dialog.addEventListener('click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }, options);
        this.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('cp-search-is-open');
          this.cancelRequest(); this.clearSuggestions(); this.trigger.focus();
        }, options);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', () => this.fitDialog(), options);
          window.visualViewport.addEventListener('scroll', () => this.fitDialog(), options);
        }
      }
      this.addEventListener('focusout', event => {
        if (!this.dialog && !this.contains(event.relatedTarget)) { this.cancelRequest(); this.clearSuggestions(); }
      }, options);
    }
    disconnectedCallback() {
      this.cancelRequest(); this.events?.abort(); this.events = null;
      if (this.results) this.clearSuggestions();
      if (this.dialog?.open) this.dialog.close();
      if (!document.querySelector('.cp-search__dialog[open]')) document.documentElement.classList.remove('cp-search-is-open');
    }
    cancelRequest() {
      clearTimeout(this.timer); this.request?.abort(); this.sequence++;
      this.removeAttribute('aria-busy');
    }
    fitDialog() {
      if (!this.dialog?.open || !window.visualViewport) return;
      this.dialog.style.setProperty('--cp-search-viewport-height', `${window.visualViewport.height}px`);
      this.dialog.style.setProperty('--cp-search-viewport-top', `${window.visualViewport.offsetTop}px`);
    }
    clearSuggestions() {
      this.results.replaceChildren(); this.results.hidden = true;
      this.status.textContent = ''; this.selected = -1;
    }
    onInput() {
      this.cancelRequest(); this.clearSuggestions();
      const query = this.input.value.trim();
      this.reset.hidden = !this.input.value;
      if (this.browse) this.browse.hidden = Boolean(query);
      if (!query || this.dataset.suggestionsEnabled === 'false') return;
      this.timer = setTimeout(() => this.suggest(query), 200);
    }
    async suggest(query) {
      const sequence = this.sequence;
      this.status.textContent = 'Searching…'; this.setAttribute('aria-busy', 'true');
      try {
        let markup = this.cache.get(query);
        if (!markup) {
          this.request = new AbortController();
          const endpoint = new URL(this.dataset.suggestUrl, location.origin);
          endpoint.searchParams.set('q', query);
          endpoint.searchParams.set('resources[type]', 'product');
          endpoint.searchParams.set('resources[limit]', '6');
          endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
          endpoint.searchParams.set('section_id', 'cp-predictive-search');
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) endpoint.searchParams.set('preview_theme_id', preview);
          const response = await fetch(endpoint.href, { signal: this.request.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Suggestions unavailable');
          markup = await response.text();
        }
        if (sequence !== this.sequence || !this.isConnected || query !== this.input.value.trim()) return;
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        const result = documentFragment.querySelector('[data-cp-predictive-result]');
        if (!result) throw new Error('Search section missing');
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, markup);
        this.results.replaceChildren(result);
        const viewAll = document.createElement('button');
        viewAll.type = 'submit'; viewAll.className = 'cp-search__view-all';
        // The button belongs to the real GET form, including type=product and prefix.
        this.form.id = this.input.id + '-form';
        viewAll.setAttribute('form', this.form.id); viewAll.textContent = 'View all results →';
        this.results.append(viewAll); this.results.hidden = false;
        this.status.textContent = Number(result.dataset.count) ? `${result.dataset.count} product suggestions` : 'No product suggestions. Press Enter to search all products.';
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence && this.isConnected) {
          this.status.textContent = 'Suggestions are unavailable. Press Search to see all results.';
        }
      } finally {
        if (sequence === this.sequence) this.removeAttribute('aria-busy');
      }
    }
    onKey(event) {
      if (event.key === 'Escape') {
        if (this.dialog?.open) {
          // A search input can consume Escape to clear its value before a dialog closes.
          event.preventDefault(); this.dialog.close();
        } else { this.cancelRequest(); this.clearSuggestions(); }
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = [...this.results.querySelectorAll('[data-cp-product], .cp-search__view-all')];
      if (!links.length || this.results.hidden) return;
      if (event.target !== this.input && !links.includes(event.target)) return;
      event.preventDefault();
      const index = links.indexOf(event.target);
      const next = event.key === 'ArrowDown' ? index + 1 : (index === -1 ? links.length - 1 : index - 1);
      (next < 0 || next >= links.length ? this.input : links[next]).focus();
    }
  }
  customElements.define('cp-product-search', ProductSearch);
})();
